import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { allowedDropHours, STANDBY_DROP_HOURS, standbyOutlook } from '@jupi/shared'
import { formatRange } from '@jupi/ui'
import type { Booking } from '../api/queries'

// Shown when the slot is taken: who has it, who is already waiting for it,
// how the standby works, and when to give up on it. The member can go on
// standby with the button beside Save.
export function StandbyOffer({
  conflicts,
  bookings,
  registration,
  window,
  dropHours,
  onDropHours,
}: {
  conflicts: readonly Booking[]
  bookings: readonly Booking[]
  registration: string
  window: { startsAt: Date; endsAt: Date } | null
  // '' is "keep it"; otherwise hours before the booking starts.
  dropHours: number | ''
  onDropHours: (value: number | '') => void
}) {
  const { t } = useTranslation()
  const ahead = window ? standbyOutlook(bookings, registration, window).ahead.length : 0
  // Only the drop times still ahead of now; one chosen earlier that the time
  // has since overtaken goes back to "keep it".
  const choices = window ? allowedDropHours(STANDBY_DROP_HOURS, window.startsAt, new Date()) : [...STANDBY_DROP_HOURS]
  useEffect(() => {
    if (dropHours !== '' && !choices.includes(dropHours)) onDropHours('')
  }, [dropHours, choices, onDropHours])

  return (
    <Alert severity="info">
      <AlertTitle>{t('rules.overlaps_existing')}</AlertTitle>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t('booking.standby.holdingTitle')}
      </Typography>
      <Stack component="ul" sx={{ pl: 2, m: 0, mb: 1 }}>
        {conflicts.map((conflict) => (
          <li key={conflict.bookingId}>
            {formatRange(conflict.startsAt, conflict.endsAt)} · {conflict.displayName}
          </li>
        ))}
      </Stack>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {ahead > 0 ? t('booking.standby.ahead', { count: ahead }) : t('booking.standby.noneAhead')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2.5 }}>
        {t('rules.offerOption')} {t('booking.standby.how')}
      </Typography>
      <TextField
        select
        fullWidth
        size="small"
        label={t('booking.standby.dropLabel')}
        value={dropHours}
        onChange={(e) => onDropHours(e.target.value === '' ? '' : Number(e.target.value))}
        // No background of its own: a rectangle behind the rounded outline shows
        // as white corners on the panel's tint. The label notch cuts the border.
        sx={{ mt: 1 }}
        // "Keep it" is a real choice with its own words, not an empty box.
        slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
      >
        <MenuItem value="">{t('booking.standby.dropNever')}</MenuItem>
        {choices.map((hours) => (
          <MenuItem key={hours} value={hours}>
            {hours === 0 ? t('booking.standby.dropAtStart') : t('booking.standby.dropBefore', { hours })}
          </MenuItem>
        ))}
      </TextField>
    </Alert>
  )
}
